import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Power, QrCode, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from '../../components/toasts';
import { TeamBadge } from '../../components/TeamBadge';
import { Badge, Button, ErrorText, Field, Input, Modal, Spinner } from '../../components/ui';
import { errorMessage, get, patch, post } from '../../lib/api';
import { formatPhone } from '../../lib/format';
import { ChannelAccount, Team } from '../../lib/types';

const STATUS: Record<ChannelAccount['status'], { label: string; tone: 'ok' | 'warn' | 'danger' }> = {
  connected: { label: 'conectado', tone: 'ok' },
  connecting: { label: 'aguardando QR code', tone: 'warn' },
  disconnected: { label: 'desconectado', tone: 'danger' },
};

/** Números de WhatsApp (instâncias da Evolution): cadastrar, conectar por QR code, desconectar. */
export function ChannelsAdmin() {
  const qc = useQueryClient();
  const channels = useQuery({ queryKey: ['channels'], queryFn: () => get<ChannelAccount[]>('/channels') });
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => get<Team[]>('/teams') });
  const [creating, setCreating] = useState(false);
  const [connecting, setConnecting] = useState<ChannelAccount | null>(null);
  const [renaming, setRenaming] = useState<ChannelAccount | null>(null);

  const refresh = () => void qc.invalidateQueries({ queryKey: ['channels'] });
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok, 'ok');
      refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const teamsOf = (id: string) => (teams.data ?? []).filter((t) => t.channelIds.includes(id));

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-fg-soft">Cada número é uma instância na Evolution. As equipes que atendem cada número são definidas na aba Equipes; com mais de uma equipe, o cliente escolhe num menu.</p>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus className="size-4" /> Novo número
        </Button>
      </div>
      {channels.isLoading && <Spinner />}
      <ErrorText error={channels.error} />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(channels.data ?? []).map((c) => {
          const status = STATUS[c.status] ?? STATUS.disconnected;
          return (
            <div key={c.id} className={`rounded-lg border border-border bg-panel p-4 ${c.active ? '' : 'opacity-60'}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 font-semibold">
                    {c.name}
                    <button className="text-fg-soft hover:text-fg" onClick={() => setRenaming(c)} aria-label="Renomear">
                      <Pencil className="size-3.5" />
                    </button>
                  </div>
                  <div className="text-xs text-fg-soft">instância: {c.externalId}</div>
                </div>
                <Badge tone={status.tone}>{status.label}</Badge>
              </div>
              <div className="mt-2 text-sm">{c.phone ? formatPhone(c.phone) : <span className="text-fg-soft">número ainda não conectado</span>}</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {teamsOf(c.id).length ? teamsOf(c.id).map((t) => <TeamBadge key={t.id} name={t.name} />) : <span className="text-xs text-fg-soft">sem equipe (fila geral)</span>}
                {teamsOf(c.id).length > 1 && <Badge>menu de escolha</Badge>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {c.status !== 'connected' && (
                  <Button size="sm" variant="primary" onClick={() => setConnecting(c)}>
                    <QrCode className="size-3.5" /> Conectar
                  </Button>
                )}
                <Button size="sm" onClick={() => void act(() => post(`/channels/${c.id}/refresh`), 'Status atualizado')}>
                  <RefreshCw className="size-3.5" /> Atualizar
                </Button>
                {c.status === 'connected' && (
                  <Button
                    size="sm"
                    onClick={() => {
                      if (confirm(`Desconectar o número de "${c.name}"? O celular sai da sessão e será preciso ler o QR code de novo.`)) {
                        void act(() => post(`/channels/${c.id}/logout`), 'Número desconectado');
                      }
                    }}
                  >
                    <Power className="size-3.5" /> Desconectar
                  </Button>
                )}
                <label className="ml-auto flex items-center gap-1.5 text-xs">
                  <input type="checkbox" checked={c.active} onChange={(e) => void act(() => patch(`/channels/${c.id}`, { active: e.target.checked }), e.target.checked ? 'Número ativado' : 'Número desativado')} />
                  ativo
                </label>
              </div>
            </div>
          );
        })}
      </div>
      {creating && (
        <CreateChannelModal
          onClose={() => setCreating(false)}
          onCreated={(c) => {
            refresh();
            setCreating(false);
            if (c.status !== 'connected') setConnecting(c);
          }}
        />
      )}
      {connecting && <ConnectModal channel={connecting} onClose={() => (setConnecting(null), refresh())} />}
      {renaming && <RenameModal channel={renaming} onClose={() => (setRenaming(null), refresh())} />}
    </div>
  );
}

function CreateChannelModal({ onClose, onCreated }: { onClose: () => void; onCreated: (c: ChannelAccount) => void }) {
  const [name, setName] = useState('');
  const [instance, setInstance] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const c = await post<ChannelAccount>('/channels/whatsapp', { name: name.trim(), instance: instance.trim() });
      toast('Número cadastrado na Evolution', 'ok');
      onCreated(c);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open
      title="Novo número de WhatsApp"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={saving} onClick={() => void submit()} disabled={!name.trim() || !instance.trim()}>
            Cadastrar e conectar
          </Button>
        </>
      }
    >
      <Field label="Nome">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: WhatsApp Suporte" autoFocus />
      </Field>
      <Field label="Instância na Evolution" hint="(letras, números, - e _; 3 a 41 caracteres)">
        <Input value={instance} onChange={(e) => setInstance(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))} placeholder="Ex.: suporte" />
      </Field>
      <p className="text-xs text-fg-soft">Se a instância já existir na Evolution, ela passa a ser usada. Depois de cadastrar, leia o QR code com o WhatsApp do celular.</p>
      <ErrorText error={error} />
    </Modal>
  );
}

/** QR code para ler no celular; atualiza o status a cada 3 s até conectar. */
function ConnectModal({ channel, onClose }: { channel: ChannelAccount; onClose: () => void }) {
  const [qr, setQr] = useState<string | null>(null);
  const [status, setStatus] = useState(channel.status);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let alive = true;
    post<{ qrCodeDataUrl: string | null; account: ChannelAccount }>(`/channels/${channel.id}/connect`)
      .then((r) => {
        if (!alive) return;
        setQr(r.qrCodeDataUrl);
        setStatus(r.account.status);
      })
      .catch((err) => alive && setError(err));
    const timer = setInterval(() => {
      post<ChannelAccount>(`/channels/${channel.id}/refresh`)
        .then((a) => alive && setStatus(a.status))
        .catch(() => undefined);
    }, 3_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [channel.id]);

  useEffect(() => {
    if (status === 'connected') {
      toast(`${channel.name} conectado`, 'ok');
      onClose();
    }
  }, [status, channel.name, onClose]);

  return (
    <Modal open title={`Conectar: ${channel.name}`} onClose={onClose} footer={<Button onClick={onClose}>Fechar</Button>}>
      <p className="mb-3 text-sm">
        No celular: <b>WhatsApp → Aparelhos conectados → Conectar um aparelho</b> e leia o código abaixo.
      </p>
      <div className="flex min-h-64 items-center justify-center rounded-md bg-white p-3">
        {qr ? <img src={qr} alt="QR code do WhatsApp" className="size-64" /> : error ? null : <Spinner />}
      </div>
      <p className="mt-2 text-center text-xs text-fg-soft">O código muda a cada poucos segundos; esta janela fecha sozinha quando conectar.</p>
      <ErrorText error={error} />
    </Modal>
  );
}

function RenameModal({ channel, onClose }: { channel: ChannelAccount; onClose: () => void }) {
  const [name, setName] = useState(channel.name);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await patch(`/channels/${channel.id}`, { name: name.trim() });
      toast('Nome atualizado', 'ok');
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open
      title="Renomear número"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={saving} disabled={!name.trim()} onClick={() => void save()}>
            Salvar
          </Button>
        </>
      }
    >
      <Field label="Nome">
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}
